import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DropdownService } from 'src/app/services/dropdown.service';
import { FileStorageService } from 'src/app/services/file-storage.service';
import { ResumeService } from 'src/app/services/resume.service';
import { Resume } from 'src/app/models/resume/resume';
import { AlertService } from 'src/app/services/alert.service';
import { AuthService } from 'src/app/services/auth.service';
import { AddressDialogComponent } from '../address-dialog/address-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ViewModalEnum } from 'src/app/enums/operation';
import {
  AddAddress,
  GetAddress,
  RegisterRequest,
} from 'src/app/models/security/register-request';
import { UserRoleIds } from 'src/app/models/security/user-role-enum';
import {
  DropdownOption,
  DropDownKeywords,
} from 'src/app/models/shared/dropdown-option';
import {
  passwordValidator,
  matchPasswords,
} from 'src/app/validators/password-validation';
import { GetCandidate } from 'src/app/models/candidate/get-candidate';
import { FormErrorsComponent } from 'src/app/components/form-errors/form-errors.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { DropdownComponent } from 'src/app/components/shared/dropdown/dropdown.component';
import { MAX_FILE_SIZE } from 'src/app/constants/file.constants';
import { CountryListDropdownComponent } from 'src/app/app/components/shared/country-list-dropdown/country-list-dropdown.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    FormsModule,
    FormErrorsComponent,
    NgxMaskDirective,
    DropdownComponent,
    CountryListDropdownComponent,
  ],
  selector: 'app-register-form-natural-person',
  templateUrl: './register-form-natural-person.component.html',
  styles: [],
})
export class RegisterFormNaturalPersonComponent implements OnInit, OnChanges {
  @Input() showSaveButton: boolean = false;
  @Input() showSaveRegister: boolean = false;

  @Input() isEditMode: boolean = false;
  @Input() isViewMode: boolean = false;
  @Input() candidateData: GetCandidate | null | undefined = null;
  @Input() candidateAddresses: GetAddress[] = [];

  @Input() typePerson: number | null = null;

  @Output() savedEmitter = new EventEmitter<boolean>();

  isCountriesDropdownVisible = true;

  form!: FormGroup;
  addresses: AddAddress[] = [];

  typeIdentList: DropdownOption[] = [];
  nationalityList: DropdownOption[] = [];
  sexList: DropdownOption[] = [];
  professionalProfileList: DropdownOption[] = [];

  public useRDMask = {
    primary: {
      code: '',
      enabled: false,
    },
    cel: {
      code: '',
      enabled: false,
    },
  };

  // Previews
  selectedDocFile: File | null = null;
  selectedPhotoFile: File | null = null;
  photoPreview: string | null = null;
  cedulaPreview: string | null = null;
  passportPreview: string | null = null;

  // Propiedades para indicadores de carga
  isPhotoUploading: boolean = false;
  isDocUploading: boolean = false;

  // Propiedades para mensajes de error
  photoUploadError: string | null = null;
  docUploadError: string | null = null;

  // Propiedades para mensajes de éxito
  photoUploadSuccess: boolean = false;
  docUploadSuccess: boolean = false;

  docReqIndex = 0; // 0 = cédula, 1 = pasaporte

  isLoading: boolean = false;
  isSaving: boolean = false;
  rolesId = UserRoleIds;

  documentMask = '00000000000';
  maxDate = this.getMaxDate();

  readonly minAge: number = 18; // Edad mínima para registro
  wantToChangePassword: boolean = false; // Toggle for password change option

  resumes: Resume[] = [];
  loadingResumes: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dropdownService: DropdownService,
    private fileStorage: FileStorageService,
    private alert: AlertService,
    private auth: AuthService,
    private dialog: MatDialog,
    private router: Router,
    private datePipe: DatePipe,
    private _resumeService: ResumeService
  ) {}

  ngOnInit(): void {
    this.buildForm();

    this.loadResumes();
    if (this.candidateData) {
      this.useRDMask.primary.code = this.candidateData
        .primaryPhoneCountryCode as any;
      this.useRDMask.cel.code = this.candidateData.cellPhoneCountryCode as any;
    }
    this.loadDropdowns().then(() => {
      if ((this.isEditMode || this.isViewMode) && this.candidateData) {
        this.form.patchValue({
          userId: this.candidateData.userId,
          personId: this.candidateData.personId,
          email: this.candidateData.email || '',
          typeIdentificationId: this.candidateData.typeIdentificationId || '',
          identification: this.candidateData.identification || '',
          firstName: this.candidateData.firstName || '',
          middleName: this.candidateData.middleName || '',
          lastName: this.candidateData.lastName || '',
          secondLastName: this.candidateData.secondLastName || '',
          sexId: this.candidateData.sexId || '',
          birthDate: this.candidateData.birthDate
            ? this.datePipe.transform(
                this.candidateData.birthDate,
                'yyyy-MM-dd'
              )
            : null,
          nationalityId: this.candidateData.nationalityId || '',
          phoneNumber: this.candidateData.primaryPhone || '',
          cellPhone: this.candidateData.cellPhone || '',
          cellPhoneHasWhatsapp:
            this.candidateData.cellPhoneHasWhatsapp || false,
          professionalProfile: this.candidateData.professionalProfile || '',
          photoFileId: this.candidateData.photoFileId,
          cedulaFileId: this.candidateData.cedulaFileId,
          passportFileId: this.candidateData.passportFileId,
          wantToPublishOffers: this.candidateData.wantToPublishOffers || false,
          wantToSearchJobs: this.candidateData.wantToSearchJobs || false,
          isIndependentProfessional:
            this.candidateData.isIndependentProfessional || false,
          publicResumeId: this.candidateData.publicResumeId,
          professionalProfileId: this.candidateData.professionalProfileId,
        });

        if (this.candidateData.photoFileId) {
          this.photoPreview = this.fileStorage.getFileUrl(
            `${this.candidateData.photoFileId}`
          );
        }
        if (this.candidateData.cedulaFileId) {
          this.cedulaPreview = this.fileStorage.getFileUrl(
            `${this.candidateData.cedulaFileId}`
          );
        }
        if (this.candidateData.passportFileId) {
          this.passportPreview = this.fileStorage.getFileUrl(
            `${this.candidateData.passportFileId}`
          );
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (this.isEditMode || this.isViewMode) &&
      this.candidateAddresses?.length
    ) {
      this.addresses = this.candidateAddresses.map((addr) => ({
        ...addr,
        isEnabled: addr.isEnabled,
      }));
    }
  }

  buildForm() {
    //this.isViewMode = true
    // Si estamos en modo vista, no necesitamos validadores
    const validators = this.isViewMode
      ? []
      : [Validators.required, Validators.email];
    const requiredValidator = this.isViewMode ? [] : [Validators.required];
    const minAgeValidators = this.isViewMode
      ? []
      : [Validators.required, this.minAgeValidator(this.minAge)];
    const requiredTrueValidator =
      this.isViewMode || this.isEditMode ? [] : [Validators.requiredTrue];
    const passwordValidators =
      this.isViewMode || this.isEditMode ? [] : [passwordValidator];

    this.form = this.fb.group(
      {
        userId: [null],
        personId: [null],
        email: ['', validators],
        typeIdentificationId: ['', requiredValidator],
        identification: [''],
        firstName: ['', requiredValidator],
        middleName: [''],
        lastName: ['', requiredValidator],
        secondLastName: [''],
        sexId: ['', requiredValidator],
        birthDate: [null, minAgeValidators],
        nationalityId: ['', requiredValidator],
        phoneNumber: [''],
        phoneNumberData: [''],
        cellPhone: [''],
        cellPhoneData: [''],
        cellPhoneHasWhatsapp: [false],
        professionalProfile: [''],

        photoFileId: [null],
        cedulaFileId: [null],
        passportFileId: [null],

        wantToPublishOffers: [false],
        wantToSearchJobs: [false],
        isIndependentProfessional: [false],
        publicResumeId: [null],
        professionalProfileId: [null],
        password: ['', passwordValidators],
        confirmPassword: [''],
        changePassword: [false],
        privacityTermsAccepted: [false, requiredTrueValidator],
        refundTermsAccepted: [false, requiredTrueValidator],
      },
      {
        validators:
          this.isViewMode || this.isEditMode
            ? []
            : [matchPasswords('password', 'confirmPassword')],
      }
    );

    // Si estamos en modo vista, deshabilitamos todos los controles
    if (this.isViewMode) {
      this.form.disable();
    }

    if (this.isEditMode) {
      this.form.get('changePassword')?.valueChanges.subscribe((value) => {
        this.wantToChangePassword = value;
        const passwordControl = this.form.get('password');
        const confirmPasswordControl = this.form.get('confirmPassword');

        if (value) {
          passwordControl?.setValidators([passwordValidator]);
          confirmPasswordControl?.setValidators([Validators.required]);
          // We need to set the form-level validator for password matching
          this.form.setValidators([
            matchPasswords('password', 'confirmPassword'),
          ]);
        } else {
          passwordControl?.clearValidators();
          confirmPasswordControl?.clearValidators();
          this.form.clearValidators();
        }

        passwordControl?.updateValueAndValidity();
        confirmPasswordControl?.updateValueAndValidity();
        this.form.updateValueAndValidity();
      });
    }
  }

  loadResumes(): void {
    // Solo cargar currículums en modo edición
    if (this.isEditMode) {
      this.loadingResumes = true;
      this._resumeService.getResumes().subscribe({
        next: (resumes) => {
          this.resumes = resumes;
          this.loadingResumes = false;
        },
        error: (error) => {
          console.error('Error al cargar currículums:', error);
          this.loadingResumes = false;
        },
      });
    }
  }

  onCountryChange(event: any, type: any) {
    if (!event?.code) {
      return;
    }
    const isRD = event.code === 'DO';
    switch (type) {
      case 'primary':
        this.useRDMask.primary.enabled = isRD;
        break;
      case 'cel':
        this.useRDMask.cel.enabled = isRD;
        break;
      default:
        this.useRDMask.primary.enabled = isRD;
        this.useRDMask.cel.enabled = isRD;
        break;
    }
  }
  onIndependentProfessionalChange(): void {
    const isIndependentProfessional = this.form.get(
      'isIndependentProfessional'
    )?.value;

    if (isIndependentProfessional) {
      // Solo cargar currículums si estamos en modo edición
      // En modo registro, el usuario aún no tiene currículums
      if (this.isEditMode && this.resumes.length === 0 && !this.loadingResumes) {
        this.loadResumes();
      }
    } else {
      // Si desmarca la opción, limpiar el valor
      this.form.get('publicResumeId')?.setValue(null);
    }
  }

  onDocumentTypeChange(): void {
    const identificationtype = this.form.value.typeIdentificationId;
    if (identificationtype) {
      const found = this.typeIdentList.find(
        (c) => c.id === +identificationtype
      );
      const relatedId = found?.relatedId ?? 0;
      // Si relatedId = 1, permitir alfanumérico (combinación de letras y números) sin máscara; si no, solo números
      this.documentMask = relatedId === 1 ? '' : '00000000000';
    } else {
      this.documentMask = '00000000000'; // Valor por defecto
    }
  }

  loadDropdowns(): Promise<void> {
    return new Promise<void>((resolve) => {
      let completedCalls = 0;
      const totalCalls = 4;

      const checkAllCompleted = () => {
        completedCalls++;
        if (completedCalls === totalCalls) {
          resolve();
        }
      };

      this.dropdownService
        .GetOptions(DropDownKeywords.TypeIdentification)
        .subscribe({
          next: (res) => {
            this.typeIdentList = res;
            checkAllCompleted();
          },
          error: () => checkAllCompleted(),
        });

      this.dropdownService
        .GetOptions(DropDownKeywords.NationalityId)
        .subscribe({
          next: (res) => {
            this.nationalityList = res;
            checkAllCompleted();
          },
          error: () => checkAllCompleted(),
        });

      this.dropdownService.GetOptions(DropDownKeywords.Sex).subscribe({
        next: (res) => {
          this.sexList = res;
          checkAllCompleted();
        },
        error: () => checkAllCompleted(),
      });

      this.dropdownService
        .GetOptions(DropDownKeywords.ProfessionalProfiles)
        .subscribe({
          next: (res) => {
            this.professionalProfileList = res;
            checkAllCompleted();
          },
          error: () => checkAllCompleted(),
        });
    });
  }

  get personAge(): string {
    const b = this.form.get('birthDate')?.value;
    if (!b) return '';
    const bdate = new Date(b);
    const diff = Date.now() - bdate.getTime();
    const age = new Date(diff).getUTCFullYear() - 1970;
    return age > 0 ? age.toString() : '';
  }

  get isSubmitButtonDisabled(): boolean {
    return (this.form.invalid && this.form.touched) || this.isLoading;
  }

  get birthDate(): AbstractControl {
    return this.form.get('birthDate')!;
  }

  // Password validation helper methods
  hasUppercaseError(): boolean {
    const password = this.form.get('password')?.value;
    return !password || !/[A-Z]/.test(password);
  }

  hasLowercaseError(): boolean {
    const password = this.form.get('password')?.value;
    return !password || !/[a-z]/.test(password);
  }

  hasDigitError(): boolean {
    const password = this.form.get('password')?.value;
    return !password || !/[0-9]/.test(password);
  }

  getMaxDate(): string {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split('T')[0]; // formato yyyy-MM-dd
  }

  minAgeValidator(minAge: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const birthDate = new Date(value);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      const d = today.getDate() - birthDate.getDate();

      const isUnderage =
        age < minAge || (age === minAge && (m < 0 || (m === 0 && d < 0)));
      return isUnderage ? { underage: true } : null;
    };
  }

  // FOTO PRINCIPAL
  uploadFoto() {
    document.getElementById('fileFoto')?.click();
  }

  onPhotoSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }
    if (!this.validatePhotoFileType(file)) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes JPG o PNG para la foto'
      );
      input.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }
    this.selectedPhotoFile = file;
    this.photoPreview = URL.createObjectURL(file);
  }

  removePhoto() {
    this.selectedPhotoFile = null;
    this.photoPreview = null;
  }

  // DOCUMENTOS: Documento de identidad (siempre se guarda en ambos campos)
  onDocReqClick(idx: number) {
    this.docReqIndex = idx;
    document.getElementById('fileDocReq')?.click();
  }

  onDocReqSelected(event: any) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      input.value = '';
      return;
    }
    if (!this.validateFileType(file)) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        'Solo se permiten imágenes o PDF para documentos'
      );
      input.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.alert.showWarningAlert(
        'Archivo inválido',
        `El archivo excede el tamaño máximo permitido (${(
          MAX_FILE_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`
      );
      input.value = '';
      return;
    }
    this.selectedDocFile = file;
    this.cedulaPreview = URL.createObjectURL(file);
  }

  removeDoc() {
    this.selectedDocFile = null;
    this.cedulaPreview = null;
  }

  // Obtener el nombre del documento según el tipo de identificación seleccionado
  getIdentificationDocumentName(): string {
    const typeIdControl = this.form.get('typeIdentificationId');
    if (!typeIdControl || !typeIdControl.value) return 'Documento de identidad';

    const selectedTypeId = typeIdControl.value;
    const selectedType = this.typeIdentList.find((type) => {
      // Verificar si el valor coincide (puede ser id o value dependiendo de la estructura)
      return type.id === selectedTypeId || type.id === +selectedTypeId;
    });
    return selectedType ? selectedType.text : 'Documento de identidad';
  }

  // Validación específica para fotos (solo JPG y PNG)
  validatePhotoFileType(file: File): boolean {
    const allowedPhotoTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    return allowedPhotoTypes.includes(file.type);
  }

  // Validación para documentos (imágenes y PDF)
  validateFileType(file: File): boolean {
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    return allowed.includes(file.type);
  }

  // DIRECCIONES (usar MatDialog con AddressDialogComponent)
  openAddressDialog() {
    const dialogRef = this.dialog.open(AddressDialogComponent, {
      width: '70%',
      maxWidth: '100%',
      data: {
        addresses: this.addresses,
        mode: ViewModalEnum.Create,
        address: null,
        index: null,
      },
    });
    dialogRef.afterClosed().subscribe((result: AddAddress[] | null) => {
      if (result) {
        this.addresses = result;
        console.log(result);
      }
    });
  }

  editAddress(address: AddAddress, index: number) {
    const dialogRef = this.dialog.open(AddressDialogComponent, {
      width: '70%',
      maxWidth: '100%',
      data: {
        addresses: this.addresses,
        mode: ViewModalEnum.Edit,
        address,
        index,
      },
    });
    dialogRef.afterClosed().subscribe((result: AddAddress[] | null) => {
      if (result) {
        this.addresses = result;
      }
    });
  }

  viewAddress(address: AddAddress) {
    this.dialog.open(AddressDialogComponent, {
      width: '70%',
      maxWidth: '100%',
      data: {
        addresses: this.addresses,
        mode: ViewModalEnum.View,
        address,
        index: null,
      },
    });
  }

  removeAddress(i: number) {
    if (!this.addresses[i].id || this.addresses[i].id === 0) {
      this.addresses.splice(i, 1);
    } else {
      this.addresses[i].isEnabled = false;
    }
  }

  get visibleAddresses() {
    return this.addresses.filter((addr) => addr.isEnabled !== false);
  }

  // GUARDAR / CREAR CUENTA
  async onSubmit() {
    if (this.isViewMode) {
      // En modo vista no hacemos validaciones ni envíos
      return;
    }

    if (!this.form.valid) {
      const missingFields = this.getMissingRequiredFields(this.form);
      this.alert.showWarningAlert(
        'Verifique los campos del formulario',
        'Debe completar los campos obligatorios'
      );
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.value;

    if (!v.identification || v.identification?.length < 6) {
      this.alert.showWarningAlert(
        'Verifique el número de identificación',
        'El número de identificación no es válido'
      );
      return;
    }

    // Prevenir múltiples envíos
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.isLoading = true;
    try {
      // Subir foto si hay archivo seleccionado localmente
      let photoFileId = v.photoFileId;
      if (this.selectedPhotoFile) {
        this.isPhotoUploading = true;
        const res = await this.fileStorage
          .uploadFile(this.selectedPhotoFile)
          .toPromise();
        if (res && res.fileId) {
          photoFileId = res.fileId;
        }
        this.isPhotoUploading = false;
      }
      // Subir documento si hay archivo seleccionado localmente
      let cedulaFileId = v.cedulaFileId;
      if (this.selectedDocFile) {
        this.isDocUploading = true;
        const res = await this.fileStorage
          .uploadFile(this.selectedDocFile)
          .toPromise();
        if (res && res.fileId) {
          cedulaFileId = res.fileId;
        }
        this.isDocUploading = false;
      }

      // Validar que el documento de identidad sea obligatorio
      if (!cedulaFileId) {
        const documentName = this.getIdentificationDocumentName();
        this.alert.showWarningAlert(
          'Documento requerido',
          `Debe subir una copia de su ${documentName} para guardar o crear la cuenta`
        );
        this.isLoading = false;
        this.isSaving = false;
        return;
      }

      const request: RegisterRequest = {
        userId: +v.userId || 0,
        userName: v.email,
        firstName: v.firstName,
        middleName: v.middleName,
        lastName: v.lastName,
        secondLastName: v.secondLastName,
        email: v.email,
        typeIdentificationId: +v.typeIdentificationId,
        identification: v.identification,
        roleId: this.typePerson || UserRoleIds.Member, // Persona
        phoneNumber: v.phoneNumber,
        password: v.password,
        changePassword: v.changePassword,
        privacityTermsAccepted: v.privacityTermsAccepted,
        refundTermsAccepted: v.refundTermsAccepted,
        extendedData: {
          personId: +v.personId || 0,
          nationalityId: +v.nationalityId,
          professionalProfile: v.professionalProfile,
          cedulaFileId: cedulaFileId,
          passportFileId: v.passportFileId,
          wantToSearchJobs: v.wantToSearchJobs,
          isIndependentProfessional: v.isIndependentProfessional,
          // Solo incluir publicResumeId si tiene un valor válido
          publicResumeId: v.publicResumeId && v.publicResumeId !== '' ? +v.publicResumeId : undefined,
          wantToPublishOffers: v.wantToPublishOffers,
          photoFileId: photoFileId,
          sexId: +v.sexId,
          birthDate: v.birthDate,
          primaryPhone: v.phoneNumber,
          primaryPhoneCountryCode: v.phoneNumberData.code,
          primaryPhoneISO2: v.phoneNumberData.isoCode,
          primaryPhoneE164: v.phoneNumberData.dialling_code + v.phoneNumber,
          primaryPhoneDialCode: v.phoneNumberData.dialling_code,
          cellPhone: v.cellPhone,
          cellPhoneCountryCode: v.cellPhoneData.code,
          cellPhoneISO2: v.cellPhoneData.isoCode,
          cellPhoneE164: v.cellPhoneData.dialling_code + v.phoneNumber,
          cellPhoneDialCode: v.cellPhoneData.dialling_code,
          cellPhoneHasWhatsapp: v.cellPhoneHasWhatsapp,
          professionalProfileId: v.professionalProfileId && v.professionalProfileId !== '' ? +v.professionalProfileId : undefined,
        },
        addresses: [...this.addresses],
      };

      if (!this.isEditMode) {
        this.auth
          .register(request)
          .subscribe({
            next: () => {
              this.alert.showSuccessAlert('Registro exitoso');
              this.savedEmitter.emit(true);
              this.router.navigate(['/login']);
            },
            error: (err) => {
              if (err) {
                this.alert.showErrorAlert(err[0]);
              } else {
                this.alert.showErrorAlert('Error al registrar');
              }
            },
          })
          .add(() => {
            this.isLoading = false;
            this.isSaving = false;
          });
      } else {
        this.auth
          .updateAccount(request)
          .subscribe({
            next: () => {
              this.alert.showSuccessAlert('Actualización exitosa');
              this.savedEmitter.emit(true);
            },
            error: (err) => {
              if (err) {
                this.alert.showErrorAlert(err[0]);
              } else {
                this.alert.showErrorAlert('Error al actualizar');
              }
            },
          })
          .add(() => {
            this.isLoading = false;
            this.isSaving = false;
          });
      }
    } catch (err) {
      this.isLoading = false;
      this.isSaving = false;
      this.alert.showErrorAlert('Error al subir archivos. Intente nuevamente.');
      console.error('Error al subir archivos:', err);
    }
  }

  getMissingRequiredFields(form: FormGroup): string[] {
    const missingFields: string[] = [];

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);

      if (control && control.invalid && control.errors?.['required']) {
        missingFields.push(key); // Store the name of the missing required field
      }
    });

    return missingFields;
  }
}
